import * as React from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { mergeStyleSets, Stack } from "@fluentui/react";
import { DirectionalHint } from "@fluentui/react/lib/ContextualMenu";
import type { ITheme } from "@fluentui/react/lib/Styling";
import { findIndex } from "@fluentui/react/lib/Utilities";

import { AppFlyoutAnchor } from "@1js/acui-button";
import type { MenuOpenOnExecuteParameter } from "@1js/acui-common";
import {
  dataUniqueId,
  executeFunctionAfterHandler,
  idWithSuffix,
  UILocation,
} from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import { AppContext } from "@1js/acui-context";
import {
  isResponsiveRibbonScalingSLRFeatureEnabled,
  isResponsiveRibbonScalingMLRFeatureEnabled,
} from "@1js/acui-experiments";
import { getDownChevronIcon } from "@1js/acui-icons";
import { MeasuredContext } from "@1js/acui-resize-group";
import { shouldShadowRender } from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";

import type { AppGalleryStyles } from "../AppGallery";
import {
  galleryButtonInPreviewTransitionClassName,
  galleryButtonInPreviewTransitionDuration,
  getAppGalleryStyles,
  getGalleryClassNames,
  getHiddenFlyoutStyles,
  getMergedGalleryAndButtonStyles,
} from "../AppGallery";
import type { AppGalleryButtonProps } from "../AppGalleryButton";
import { AppGalleryButton } from "../AppGalleryButton";
import type { AppPreviewGalleryInternalProps } from "./AppPreviewGallery.Props";
import { derivePreviewFlyoutKeytip } from "./derivePreviewFlyoutKeytip";
import { ghostFlyoutIdSuffix } from "./AppPreviewGalleryConsts";

export interface AppPreviewGalleryState {
  selectedIndex: number | undefined;
  expanded: boolean;
  focused: boolean;
  buttonFocused: boolean;
}

/**
 * The Preview Gallery is for rendering a gallery showing a set
 * of gallery buttons which gives a preview of the full gallery.
 * Preview gallery contains a button which renders the full gallery
 * (in whatever for the creator want to render it)
 */
export class AppPreviewGallery<
  T extends AppGalleryButtonProps,
> extends React.PureComponent<
  AppPreviewGalleryInternalProps<T>,
  AppPreviewGalleryState
> {
  private appFlyoutAnchorId: string = "";

  constructor(props: AppPreviewGalleryInternalProps<T>) {
    super(props);
    this.state = {
      selectedIndex: this.getSelectedIndex(
        props.galleryButtons,
        props.selectedId!
      ),
      expanded: false,
      // NOTE: To style the parent container (galleryContainer) when galleryButtons are focused we need a way to keep track of it in the component.
      // :focus-within selector is not supported by IE11 or Edge so this workaround was added to make visual changes to a parent container, once one of its children has focus.
      // Once :focus-within is supported in Edge and we no longer support IE11, we can replace this with selector based styling.
      focused: false,
      buttonFocused: false,
    };
  }

  public render() {
    const {
      id,
      disabled = false,
      automationType,
      uniqueId,
      keytipProps,
      controlInMenuRendererFunction,
      location,
      persistMenu,
      deferredMenuDefinition,
      shadowRenderMenu,
      preventMenuExecution,
      stack,
    } = this.props;
    const expanded = this.state.expanded;
    this.appFlyoutAnchorId = id + "-previewExpandButton";
    return (
      <MeasuredContext.Consumer>
        {({ isMeasured }) => (
          <AppContext.Consumer>
            {(context) => (
              <AppThemeConsumer>
                {(theme: ITheme) => {
                  const galleryStyles = getAppGalleryStyles(
                    theme,
                    this.props.styles,
                    undefined
                  );
                  const galleryClassNames = getGalleryClassNames(
                    theme,
                    galleryStyles.galleryContainer,
                    expanded,
                    this.state.focused,
                    disabled,
                    this.state.buttonFocused,
                    !!stack
                  );
                  return (
                    <>
                      {this.renderHiddenFlyout(theme, context)}
                      <div
                        id={id}
                        className={galleryClassNames.galleryContainer}
                        data-automation-type={automationType}
                        data-unique-id={dataUniqueId({
                          id: uniqueId!,
                          location,
                        })}
                        aria-label={
                          this.props.flyoutAnchorProps &&
                          this.props.flyoutAnchorProps.label
                        }
                      >
                        {this.renderButtonDisplay(theme, galleryStyles)}
                        <AppFlyoutAnchor
                          type="AppFlyoutAnchorProps"
                          id={this.appFlyoutAnchorId}
                          onExecute={this.onPreviewExpandButtonExecute}
                          onAfterMenuDismiss={this.onAfterMenuDismiss}
                          onFocus={this.onFocusDownButton}
                          onBlur={this.onBlurDownButton}
                          location={this.props.location}
                          aria-haspopup={true}
                          aria-expanded={!disabled && expanded}
                          disabled={disabled}
                          icon={getDownChevronIcon(this.props)}
                          keytipProps={derivePreviewFlyoutKeytip(keytipProps)}
                          styles={galleryStyles.previewExpandButton}
                          menuDefinition={this.getMenuDefinition(persistMenu)}
                          deferredMenuDefinition={deferredMenuDefinition}
                          directionalHint={DirectionalHint.bottomRightEdge}
                          label={this.props.menuLauncherLabel}
                          hideLabel={true}
                          hideChevron={true}
                          data-automation-type={
                            this.props.dataAutomationType ||
                            "AppPreviewGalleryMenu"
                          }
                          data-unique-id={dataUniqueId(
                            { id: uniqueId!, location },
                            "previewExpandButton"
                          )}
                          controlInMenuRendererFunction={
                            controlInMenuRendererFunction
                          }
                          downChevronIcon={this.props.downChevronIcon}
                          downChevronIconSize={this.props.downChevronIconSize}
                          fancyTooltipTopMargin={3}
                          persistMenu={persistMenu}
                          shadowRenderMenu={
                            location === UILocation.Ribbon &&
                            shouldShadowRender(shadowRenderMenu, isMeasured)
                          }
                          preventMenuExecution={preventMenuExecution}
                          terminalParentControlId={this.props.id}
                        />
                      </div>
                    </>
                  );
                }}
              </AppThemeConsumer>
            )}
          </AppContext.Consumer>
        )}
      </MeasuredContext.Consumer>
    );
  }

  private onPreviewExpandButtonExecute = (
    parameter: MenuOpenOnExecuteParameter
  ) => {
    this.props.onMenuButtonExecute(parameter);
    if (!parameter.isShadowRendering) {
      this.toggleExpandedState();
    }
  };

  private onFocusDownButton = () => {
    this.setState({ buttonFocused: true });
    this.onFocus();
  };

  private onBlurDownButton = () => {
    this.setState({ buttonFocused: false });
    this.onBlur();
  };

  private onFocus = () => {
    this.setState({ focused: true });
  };

  private onBlur = () => {
    this.setState({ focused: false });
  };

  private onAfterMenuDismiss = () => {
    this.toggleExpandedState();
    if (this.props.onAfterMenuDismiss) {
      this.props.onAfterMenuDismiss(this.props.id);
    }
    // Since destroying the flyout menu by pressing Esc or clicking the button again will
    //  cause us miss the onBlur event on the button, we need to make sure we track the focus
    //  correctly - (:focus-within is unsupported by Edge and IE)
    if (
      document.activeElement &&
      document.activeElement.id !== this.appFlyoutAnchorId
    ) {
      this.onBlurDownButton();
    }
  };

  private toggleExpandedState = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  /**
   *  Renders buttons as a grid or row
   */
  private renderButtonDisplay(
    theme: ITheme,
    galleryStyles: AppGalleryStyles
  ): JSX.Element | JSX.Element[] {
    const { stack, animate } = this.props;

    const shouldRenderAsStack = !!stack;
    const buttons = !!animate ? (
      <TransitionGroup component={null}>
        {this.renderPreview(theme)}
      </TransitionGroup>
    ) : (
      this.renderPreview(theme)
    );
    if (shouldRenderAsStack) {
      return (
        <Stack
          horizontal={true}
          wrap={true}
          styles={galleryStyles.previewStackStyle}
        >
          {buttons}
        </Stack>
      );
    }
    return buttons;
  }

  /**
   * Create the list of galleryButtons to show in the preview gallery.
   */
  private renderPreview(theme: ITheme): JSX.Element[] {
    const {
      animate,
      disabled,
      galleryButtons,
      onGalleryButtonExecute,
      selectedId,
      contextMenuMap,
    } = this.props;

    // Build the actual GalleryButtons
    return galleryButtons.map((galleryButton, index) => {
      // Make sure each galleryButton has an index
      const galleryButtonProps = {
        ...galleryButton,
        index,
        id: galleryButton.id,
        disabled: disabled || galleryButton.disabled,
        onFocus: executeFunctionAfterHandler(
          this.props.onGalleryButtonFocus,
          this.onFocus
        ),
        onBlur: this.onBlur,
        onExecute: onGalleryButtonExecute,
        location: this.props.location,
        style: getAppGalleryStyles(
          theme,
          getMergedGalleryAndButtonStyles(
            this.props.styles,
            galleryButton.style
          )
        ).galleryButtonInPreview,
        selected: this.getSelectedIndex(galleryButtons, selectedId) === index,
        key: galleryButton.id + "-" + index,
        role: "radio",
        ariaLabel: galleryButton.label,
        // Task 2547546
        // Currently shouldTakeFocus will always be undefined, however we need to do major refactoring
        // to set shouldTakeFocus to the GalleryButton that we might not need to do.
        shouldKeepFocus:
          galleryButton.shouldTakeFocus !== undefined
            ? galleryButton.shouldTakeFocus
            : true,
        contextMenuMap,
        isPreviewGalleryButton: true,
      };

      const GalleryButtonTag =
        this.props.getGalleryButtonComponent || AppGalleryButton;
      // @ts-ignore: This kind of expression is always truthy
      const galleryButtonContent = (
        <GalleryButtonTag
          key={galleryButtonProps.key}
          {...(galleryButtonProps as any)}
        />
      ) || (
        <AppGalleryButton
          key={galleryButtonProps.key}
          {...(galleryButtonProps as any)}
        />
      );

      return !!animate ? (
        <CSSTransition
          key={galleryButtonProps.key}
          timeout={galleryButtonInPreviewTransitionDuration}
          classNames={galleryButtonInPreviewTransitionClassName}
          enter={animate}
          exit={animate}
        >
          {galleryButtonContent}
        </CSSTransition>
      ) : (
        galleryButtonContent
      );
    });
  }

  /**
   * Get the selected item's index
   * @param galleryButtons - The items to search
   * @param selectedId - The selected item's id to find
   * @returns {number} - The index of the selected item's id, -1 if there was no match
   */
  private getSelectedIndex(
    items: T[],
    selectedId?: string
  ): number | undefined {
    const selectedIndex = findIndex(items, (item) => item.id === selectedId);
    return selectedIndex >= 0 ? selectedIndex : undefined;
  }

  /**
   * Gets the menu definition for the preview expand button - which opens up the full gallery.
   * Note that when the persistMenu is true, we always to the menuDefinition to be be populated.
   */
  private getMenuDefinition = (persistMenu?: boolean) => {
    const { expandedOptions } = this.props;
    const { expanded } = this.state;

    return expanded || persistMenu ? expandedOptions : undefined;
  };

  /**
   * When responsive rendering is enabled, a hidden flyout anchor is rendered
   * so we can determine it's size before applying the GalleryToFlyout scaling step
   */
  private renderHiddenFlyout = (theme: ITheme, context: AppContextType) => {
    const responsiveRenderingIsEnabled =
      isResponsiveRibbonScalingSLRFeatureEnabled(
        context.isExperimentalFeatureEnabled
      ) ||
      isResponsiveRibbonScalingMLRFeatureEnabled(
        context.isExperimentalFeatureEnabled
      );

    // These props may be undefined and need to have a fallback
    const flyoutAnchorPropsWithFallback = {
      icon: this.props.flyoutAnchorProps?.icon ?? "",
      label:
        this.props.flyoutAnchorProps?.label ?? this.props.menuLauncherLabel,
    };

    const responsiveRenderingProps = responsiveRenderingIsEnabled
      ? {
          "aria-hidden": false,
          dataIsFocusable: true,
          deferredMenuDefinition: this.props.deferredMenuDefinition,
          directionalHint: DirectionalHint.bottomRightEdge,
          keytipProps: derivePreviewFlyoutKeytip(this.props.keytipProps),
          menuDefinition: this.getMenuDefinition(this.props.persistMenu),
          onExecute: this.onPreviewExpandButtonExecute,
          terminalParentControlId: this.props.id,
          onAfterMenuDismiss: this.onAfterMenuDismiss,
          ...this.props.flyoutAnchorProps,
        }
      : {};

    return (
      <AppFlyoutAnchor
        type="AppFlyoutAnchorProps"
        id={`${idWithSuffix(this.props.id, ghostFlyoutIdSuffix)}`}
        location={this.props.location}
        controlInMenuRendererFunction={this.props.controlInMenuRendererFunction}
        downChevronIcon={this.props.downChevronIcon}
        downChevronIconSize={this.props.downChevronIconSize}
        aria-hidden={true}
        styles={getHiddenFlyoutStyles(
          mergeStyleSets(
            this.getHiddenFlyoutAnchorStyles(
              theme,
              this.props.hiddenFlyoutAnchorStyles
            ),
            this.props.flyoutAnchorProps?.styles
          )
        )}
        dataIsFocusable={false}
        {...responsiveRenderingProps}
        {...flyoutAnchorPropsWithFallback}
      />
    );
  };

  private getHiddenFlyoutAnchorStyles = (
    theme: ITheme,
    styles?: IButtonStyles | ((theme: ITheme) => IButtonStyles | undefined)
  ) => {
    if (styles && typeof styles === "function") {
      return styles(theme);
    }
    return styles;
  };
}
